import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MembershipPlanResponse, MembershipPriceResponse, CompanyResponse } from '../../../../../../../../../../@shared/models/response.module';
import { EventsService } from '../../../../../../../../../../@shared/services/events-service';
import { StaticService } from '../../../../../../../../../../@shared/services/static-service';
import { firstValueFrom } from 'rxjs';

interface PriceEntry {
  plan: MembershipPlanResponse;
  price: number | null; // Always in kroner
  isSelected: boolean;
  existing_price_id?: number;
}

interface CompanyPriceGroup {
  company: CompanyResponse | null;
  companyId: string;
  companyName: string;
  companyLogo: string | null;
  entries: PriceEntry[];
  isExpanded: boolean;
  selectedCount: number;
}

@Component({
  selector: 'app-membership-pricing-modal',
  standalone: false,
  templateUrl: './membership-pricing-modal.html',
  styleUrl: './membership-pricing-modal.css'
})
export class MembershipPricingModal implements OnInit {
  @Input() itemType: 'class' | 'package' | 'service' = 'class';
  @Input() itemId: number;
  @Input() itemName: string = '';
  @Input() basePrice: number = 0;
  @Input() useCents: boolean = false; // For packages that use price_cents
  @Input() currency: string = 'NOK'; // Default currency from event

  membershipPlans: MembershipPlanResponse[] = [];
  priceEntries: PriceEntry[] = [];
  companyGroups: CompanyPriceGroup[] = [];
  loading = true;
  saving = false;

  constructor(
    private modal: NgbActiveModal,
    private eventsService: EventsService,
    private staticService: StaticService
  ) {}

  ngOnInit() {
    this.loadData();
  }

  async loadData() {
    this.loading = true;
    try {
      // Load all membership plans
      const plans = await firstValueFrom(this.staticService.getMembershipPlans());
      this.membershipPlans = plans;

      // Load existing prices for this item
      let existingPrices: MembershipPriceResponse[] = [];
      if (this.itemType === 'class') {
        existingPrices = await firstValueFrom(this.eventsService.getClassMembershipPrices(this.itemId));
      } else if (this.itemType === 'package') {
        existingPrices = await firstValueFrom(this.eventsService.getPackageMembershipPrices(this.itemId));
      } else if (this.itemType === 'service') {
        existingPrices = await firstValueFrom(this.eventsService.getServiceMembershipPrices(this.itemId));
      }

      // Create price entries for each plan
      // For packages (useCents=true), convert from øre to kroner for display
      this.priceEntries = this.membershipPlans.map(plan => {
        const existingPrice = existingPrices.find(p => p.plan_id === plan.plan_id);
        let priceInKroner: number | null = null;
        if (existingPrice) {
          // For packages, the backend stores price in øre (cents), convert to kroner
          priceInKroner = this.useCents ? existingPrice.price / 100 : existingPrice.price;
        }
        return {
          plan,
          price: priceInKroner,
          isSelected: !!existingPrice,
          existing_price_id: existingPrice?.price_id
        };
      });

      // Group entries by company/organization
      this.groupEntriesByCompany();

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      this.loading = false;
    }
  }

  groupEntriesByCompany() {
    const groupMap = new Map<string, CompanyPriceGroup>();

    for (const entry of this.priceEntries) {
      const companyId = entry.plan.company_id || 'unknown';

      if (!groupMap.has(companyId)) {
        groupMap.set(companyId, {
          company: entry.plan.company || null,
          companyId: companyId,
          companyName: entry.plan.company?.company_name || 'Unknown Organisation',
          companyLogo: entry.plan.company?.company_logo_url || null,
          entries: [],
          isExpanded: false,
          selectedCount: 0
        });
      }

      const group = groupMap.get(companyId)!;
      group.entries.push(entry);
      if (entry.isSelected) {
        group.selectedCount++;
      }
    }

    // Convert map to array and sort by company name
    this.companyGroups = Array.from(groupMap.values())
      .sort((a, b) => a.companyName.localeCompare(b.companyName));

    // Expand groups that have selected entries, or expand all if only one group
    if (this.companyGroups.length === 1) {
      this.companyGroups[0].isExpanded = true;
    } else {
      for (const group of this.companyGroups) {
        if (group.selectedCount > 0) {
          group.isExpanded = true;
        }
      }
    }
  }

  toggleGroup(group: CompanyPriceGroup) {
    group.isExpanded = !group.isExpanded;
  }

  updateGroupSelectedCount(group: CompanyPriceGroup) {
    group.selectedCount = group.entries.filter(e => e.isSelected).length;
  }

  // Get base price in kroner (converts from øre if useCents is true)
  getBasePriceInKroner(): number {
    return this.useCents ? this.basePrice / 100 : this.basePrice;
  }

  getSavingsPercent(price: number | null): number {
    const basePriceKroner = this.getBasePriceInKroner();
    if (price === null || basePriceKroner <= 0) return 0;
    return Math.round((1 - price / basePriceKroner) * 100);
  }

  formatPrice(price: number | null): string {
    if (price === null) return '-';
    // Price is always in kroner now
    return price.toFixed(2);
  }

  formatBasePrice(): string {
    // Always display in kroner
    return this.getBasePriceInKroner().toFixed(2);
  }

  togglePlan(entry: PriceEntry, group?: CompanyPriceGroup) {
    entry.isSelected = !entry.isSelected;
    if (!entry.isSelected) {
      entry.price = null;
    }
    if (group) {
      this.updateGroupSelectedCount(group);
    }
  }

  async save() {
    this.saving = true;
    try {
      // Collect all selected prices
      // Prices are entered in kroner, convert to øre for packages
      const selectedPrices = this.priceEntries
        .filter(e => e.isSelected && e.price !== null)
        .map(e => ({
          plan_id: e.plan.plan_id,
          price: e.price!, // Price in kroner for classes/services
          price_cents: this.useCents ? Math.round(e.price! * 100) : undefined // Convert kroner to øre for packages
        }));

      // Delete prices for deselected plans that had existing prices
      const deletedPrices = this.priceEntries
        .filter(e => !e.isSelected && e.existing_price_id);

      for (const entry of deletedPrices) {
        if (this.itemType === 'class') {
          await firstValueFrom(this.eventsService.deleteClassMembershipPrice(entry.existing_price_id!));
        } else if (this.itemType === 'package') {
          await firstValueFrom(this.eventsService.deletePackageMembershipPrice(entry.existing_price_id!));
        } else if (this.itemType === 'service') {
          await firstValueFrom(this.eventsService.deleteServiceMembershipPrice(entry.existing_price_id!));
        }
      }

      // Save new/updated prices
      if (selectedPrices.length > 0) {
        if (this.itemType === 'class') {
          await firstValueFrom(this.eventsService.setClassMembershipPrices(
            this.itemId,
            selectedPrices.map(p => ({ plan_id: p.plan_id, price: p.price }))
          ));
        } else if (this.itemType === 'package') {
          await firstValueFrom(this.eventsService.setPackageMembershipPrices(
            this.itemId,
            selectedPrices.map(p => ({ plan_id: p.plan_id, price_cents: p.price_cents! }))
          ));
        } else if (this.itemType === 'service') {
          await firstValueFrom(this.eventsService.setServiceMembershipPrices(
            this.itemId,
            selectedPrices.map(p => ({ plan_id: p.plan_id, price: p.price }))
          ));
        }
      }

      this.modal.close(true);
    } catch (error) {
      console.error('Error saving membership prices:', error);
    } finally {
      this.saving = false;
    }
  }

  close() {
    this.modal.dismiss();
  }
}
